import { Stack, Typography } from '@mui/material';
import MuiMarkdown from 'mui-markdown';

import useTaskDocumentDownload, { DownloadErrorReason } from './useTaskDocumentDownload';

import Dialog from 'modules/common/components/MUI/Dialog';
import Tag from 'modules/common/components/MUI/Tag';
import { DownloadContext, SubmitActionTask } from 'modules/Tasks/types';

interface DownloadModalProps {
    open: boolean;
    onClose: () => void;
    task: SubmitActionTask;
}

const ERROR_COPY: Record<DownloadErrorReason, string> = {
    download: 'Something went wrong downloading your file. Please try again.',
    // Deliberately makes no claim about the file: we either got nothing back or the export is no
    // longer in their documents, and the task stays open either way.
    unavailable: 'We could not confirm your file is ready. Please try again, or contact support if this continues.',
    completion: 'Your file downloaded, but we could not close out this task. Please try again.',
};

function isDownloadContext(ctx: unknown): ctx is DownloadContext {
    return typeof ctx === 'object' && ctx !== null && typeof (ctx as DownloadContext).document_uuid === 'string';
}

export default function DownloadModal({ open, onClose, task }: Readonly<DownloadModalProps>) {
    const context = isDownloadContext(task.context) ? task.context : null;
    // Left empty when the context omits it; the hook derives a name with a usable extension from the
    // downloaded blob rather than saving an extensionless file the OS cannot open.
    const fileName = context?.file_name ?? '';

    const { isDownloading, errorReason, handleDownload } = useTaskDocumentDownload({
        task,
        documentUuid: context?.document_uuid,
        fileName,
        open,
        onClose,
    });

    if (!context) {
        return null;
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            heading={task.task_template.display_name}
            size="medium"
            positiveAction={{
                label: 'Download',
                onClick: handleDownload,
                loading: isDownloading,
                disabled: isDownloading,
            }}>
            {context.notes && (
                <Stack spacing={1} sx={{ mb: 3 }}>
                    <Tag color="info" variant="filled" label="Notes" />
                    <MuiMarkdown>{context.notes}</MuiMarkdown>
                </Stack>
            )}
            {fileName && <Typography variant="body1">{fileName}</Typography>}
            {errorReason && (
                <Typography variant="body2" color="error" sx={{ mt: 2 }}>
                    {ERROR_COPY[errorReason]}
                </Typography>
            )}
        </Dialog>
    );
}
